import { env } from './env';
import { getIdToken } from './auth';
import { mockStore } from './mockStore';
import type { DashboardSnapshot, Capture, Pin, NowPlaying, CalendarEvent } from '@daily/shared';

export interface SpotifyDevice {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
  /** Whether the device is currently in Spotify's live device list (vs cached from a previous session). */
  isOnline: boolean;
  isRestricted: boolean;
  volumePercent: number;
}

export type SpotifyAction = 'play' | 'pause' | 'next' | 'previous' | 'transfer' | 'volume';

export interface SpotifyTrack {
  kind: 'track';
  uri: string;
  name: string;
  artists: string;
  album: string;
  artUrl: string | null;
  durationMs: number;
  explicit: boolean;
}

export interface SpotifyAlbum {
  kind: 'album';
  uri: string;
  name: string;
  artists: string;
  artUrl: string | null;
}

export interface SpotifyPlaylist {
  kind: 'playlist';
  uri: string;
  name: string;
  owner: string;
  artUrl: string | null;
}

export interface SpotifySearchResults {
  tracks: SpotifyTrack[];
  albums: SpotifyAlbum[];
  playlists: SpotifyPlaylist[];
}

export interface SonosGroup {
  id: string;
  name: string;
  coordinatorId: string;
  playerIds: string[];
  playbackState: string;
  track?: NowPlaying;
  volume?: number;
}

export interface SonosState {
  households: { id: string }[];
  players: { id: string; name: string }[];
  groups: SonosGroup[];
  activeGroupId: string | null;
  nowPlaying: NowPlaying;
}

export type SonosAction = 'play' | 'pause' | 'next' | 'previous' | 'volume' | 'group';

export type NestMode = 'HEAT' | 'COOL' | 'HEATCOOL' | 'OFF';
export type NestEcoMode = 'OFF' | 'MANUAL_ECO';
export type NestHvacStatus = 'OFF' | 'HEATING' | 'COOLING';

export interface NestZone {
  deviceName: string;
  name: string;
  online: boolean;
  ambientTempC: number | null;
  humidityPct: number | null;
  setpointC: number | null;
  mode: NestMode | null;
  hvacStatus: NestHvacStatus | null;
  ecoMode: NestEcoMode | null;
  availableModes: NestMode[];
}

export interface NestState {
  zones: NestZone[];
}

export type NestAction = 'setHeat' | 'setMode' | 'setEco';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (env.mock) {
    throw new Error(`API call to ${path} in mock mode — handle this in the caller`);
  }
  const token = await getIdToken();
  const r = await fetch(`${env.apiBase}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`API ${r.status}: ${text}`);
  }
  const body = (await r.json()) as { ok: boolean; data?: T; error?: { message: string } };
  if (!body.ok) throw new Error(body.error?.message ?? 'Unknown API error');
  return body.data as T;
}

export const api = {
  async getDashboard(): Promise<DashboardSnapshot> {
    if (env.mock) return mockStore.getDashboard();
    return request<DashboardSnapshot>('/dashboard');
  },
  async listPins(): Promise<Pin[]> {
    if (env.mock) return mockStore.getDashboard().pins;
    return request<Pin[]>('/pins');
  },
  async createCapture(captureId: string, contentType: string, byteLength: number) {
    if (env.mock) {
      return { captureId, uploadUrl: `mock://upload/${captureId}` };
    }
    return request<{ captureId: string; uploadUrl: string }>(`/captures`, {
      method: 'POST',
      body: JSON.stringify({ captureId, contentType, byteLength }),
    });
  },
  async finaliseCapture(captureId: string, hintType?: string, thumbnailUrl?: string) {
    if (env.mock) {
      const type = (hintType ?? 'DRAWING') as Capture['captureType'];
      const capture: Capture = {
        id: captureId,
        captureType: type,
        status: 'ready',
        thumbnailUrl: thumbnailUrl ?? null,
        originalUrl: thumbnailUrl ?? null,
        uploadedAt: new Date().toISOString(),
        caption: type === 'DRAWING' ? '"Just now"' : 'Pinned just now',
        author: type === 'DRAWING' ? 'siena' : undefined,
        confidence: 0.85,
        metadata: {},
      };
      mockStore.addCapture(capture);
      if (type !== 'DRAWING') {
        mockStore.addPin({
          id: `pin-${captureId}`,
          captureId,
          title: capture.caption ?? 'New pin',
          category: 'other',
          deadline: null,
          expiresAt: new Date(Date.now() + 14 * 86400_000).toISOString(),
          colourId: 'peach',
          meta: null,
          status: 'active',
          createdAt: new Date().toISOString(),
        });
      }
      return { id: captureId, status: 'ready' };
    }
    return request(`/captures/${captureId}/finalise`, {
      method: 'POST',
      body: JSON.stringify({ hintType }),
    });
  },
  async listCaptures(): Promise<Capture[]> {
    if (env.mock) return mockStore.getCaptures();
    return request<Capture[]>('/captures');
  },
  async dismissPin(pinId: string) {
    if (env.mock) {
      mockStore.dismissPin(pinId);
      return { ok: true };
    }
    return request(`/pins/${pinId}/dismiss`, { method: 'POST' });
  },
  async actionPin(pinId: string) {
    if (env.mock) {
      mockStore.actionPin(pinId);
      return { ok: true };
    }
    return request(`/pins/${pinId}/action`, { method: 'POST' });
  },
  async getNowPlaying(): Promise<NowPlaying> {
    if (env.mock) {
      return mockStore.getDashboard().nowPlaying ?? {
        playing: false, track: '', artist: '', album: null, artUrl: null,
        progressPct: 0, zone: '', updatedAt: new Date().toISOString(),
      };
    }
    return request<NowPlaying>('/spotify/now-playing');
  },
  async listSpotifyDevices(): Promise<SpotifyDevice[]> {
    if (env.mock) {
      return [
        { id: 'kitchen', name: 'Kitchen Sonos', type: 'Speaker', isActive: true, isOnline: true, isRestricted: false, volumePercent: 40 },
        { id: 'study', name: 'Study Sonos', type: 'Speaker', isActive: false, isOnline: true, isRestricted: false, volumePercent: 30 },
        { id: 'garden', name: 'Garden Planters', type: 'Speaker', isActive: false, isOnline: false, isRestricted: false, volumePercent: 20 },
      ];
    }
    return request<SpotifyDevice[]>('/spotify/devices');
  },
  async spotifyControl(
    action: SpotifyAction,
    opts?: {
      deviceId?: string;
      volumePercent?: number;
      uris?: string[];
      contextUri?: string;
    },
  ) {
    if (env.mock) return { ok: true };
    return request('/spotify/control', {
      method: 'POST',
      body: JSON.stringify({ action, ...(opts ?? {}) }),
    });
  },
  async searchSpotify(q: string): Promise<SpotifySearchResults> {
    if (env.mock) {
      return { tracks: [], albums: [], playlists: [] };
    }
    return request<SpotifySearchResults>(`/spotify/search?q=${encodeURIComponent(q)}`);
  },
  async getEvents(fromDate: string, toDate: string): Promise<CalendarEvent[]> {
    if (env.mock) return mockStore.getDashboard().agenda;
    return request<CalendarEvent[]>(`/events?from=${fromDate}&to=${toDate}`);
  },
  async getSonosState(): Promise<SonosState> {
    if (env.mock) {
      return {
        households: [{ id: 'mock' }],
        players: [
          { id: 'p1', name: 'Kitchen/Dining' },
          { id: 'p2', name: 'Living Room' },
          { id: 'p3', name: 'Bedroom Two' },
          { id: 'p4', name: 'Garden Planters' },
          { id: 'p5', name: 'Study' },
        ],
        groups: [
          {
            id: 'g1',
            name: 'Kitchen/Dining',
            coordinatorId: 'p1',
            playerIds: ['p1'],
            playbackState: 'PLAYBACK_STATE_PLAYING',
            track: {
              playing: true,
              track: 'Entre Dos Aguas',
              artist: 'Paco de Lucía',
              album: 'Fuente y Caudal',
              artUrl: null,
              progressPct: 42,
              zone: 'Kitchen/Dining',
              updatedAt: new Date().toISOString(),
            },
            volume: 40,
          },
        ],
        activeGroupId: 'g1',
        nowPlaying: {
          playing: true,
          track: 'Entre Dos Aguas',
          artist: 'Paco de Lucía',
          album: 'Fuente y Caudal',
          artUrl: null,
          progressPct: 42,
          zone: 'Kitchen/Dining',
          updatedAt: new Date().toISOString(),
        },
      };
    }
    return request<SonosState>('/sonos/state');
  },
  async sonosControl(
    action: SonosAction,
    opts?: {
      groupId?: string;
      playerId?: string;
      playerIds?: string[];
      volumePercent?: number;
    },
  ) {
    if (env.mock) return { ok: true };
    return request('/sonos/control', {
      method: 'POST',
      body: JSON.stringify({ action, ...(opts ?? {}) }),
    });
  },
  async getNestState(): Promise<NestState> {
    if (env.mock) {
      return {
        zones: [
          { deviceName: 'mock/1', name: 'Lounge', online: true, ambientTempC: 20.5, humidityPct: 45, setpointC: 21, mode: 'HEAT', hvacStatus: 'OFF', ecoMode: 'OFF', availableModes: ['HEAT', 'OFF'] },
          { deviceName: 'mock/2', name: 'Bedroom', online: true, ambientTempC: 19.0, humidityPct: 48, setpointC: 19, mode: 'HEAT', hvacStatus: 'HEATING', ecoMode: 'OFF', availableModes: ['HEAT', 'OFF'] },
          { deviceName: 'mock/3', name: 'Office', online: true, ambientTempC: 21.5, humidityPct: 42, setpointC: 20, mode: 'OFF', hvacStatus: 'OFF', ecoMode: 'OFF', availableModes: ['HEAT', 'OFF'] },
        ],
      };
    }
    return request<NestState>('/nest/state');
  },
  async nestControl(
    action: NestAction,
    opts: { deviceName: string; heatCelsius?: number; mode?: NestMode; ecoMode?: NestEcoMode },
  ) {
    if (env.mock) return { ok: true };
    return request('/nest/control', {
      method: 'POST',
      body: JSON.stringify({ action, ...opts }),
    });
  },
};
